import type { Session } from "../../rest/schema.gen.js";
import {
  describeType,
  MountayaConfigurationError,
  MountayaConnectionError,
  refuseOption,
} from "../rules/errors.js";
import { readSessionAnswer, type SessionAnswer } from "../rules/sessionAnswer.js";
import { MAX_TIMER_MS, REFRESH_MARGIN_MS, refreshSchedule } from "../rules/sessionSchedule.js";

/**
 * The session-token lifecycle: mint, schedule, refresh before expiry, back off when a token
 * arrives already spent.
 *
 * A session token scopes a publishable key up to an organization. Minting it means exchanging a
 * **secret** key, which can only happen on a server, so nothing here holds one. It takes a `mint`
 * callback that calls the application's own endpoint and owns the part that is genuinely fiddly
 * and identical for every consumer: refreshing BEFORE expiry rather than after a 401.
 *
 * Refreshing on 401 is the arrangement people reach for first, and it fails in the way that is
 * hardest to reproduce: the token expires mid-flight on one request out of a burst, so one call
 * in a page load fails while its neighbours succeed.
 *
 * It is a state machine and no framework's work: a generation counter, a chained timer and a
 * doubling backoff. None of the subtleties it records is a framework decision: a long wait is
 * walked down in hops so a suspended tab wakes to a re-read clock, the refresh margin is capped
 * at half a token's life so a thirty-second token does not schedule at zero, and each
 * consecutive spent token doubles the wait so a skewed clock costs the application's endpoint
 * one slow loop instead of a call every five seconds. So the machine is here, in the package
 * that has no framework, and a React, Vue, Svelte or Solid hook is a binding over it.
 * `getSnapshot` and `subscribe` are `useSyncExternalStore`'s two arguments exactly. Nothing in
 * this file imports React or touches a DOM node, so a Node process, a worker, or a CLI that
 * holds a long-lived client can keep a token fresh the same way a component does.
 */

/**
 * SessionTokenSnapshot is what a surface reads, and what `useSyncExternalStore` compares by
 * reference.
 *
 * One object rather than four subscriptions, and it is replaced rather than mutated on every
 * change, so a binding that holds the previous one can tell that something moved.
 */
export interface SessionTokenSnapshot {
  /**
   * The last mint failure: what the mint rejected with, or a `MountayaConnectionError` where it
   * answered no session or did not answer within the margin. Minting is retried on schedule
   * regardless.
   */
  readonly error: unknown;
  /**
   * When the current token expires, which can be an instant already PAST when the server hands
   * one back that way: the engine answers that by backing off between mints rather than
   * re-minting on a fixed floor.
   */
  readonly expiresAt: Date | undefined;
  /**
   * Whether a token is on its way: a mint is out, or the engine is enabled, holds no token and
   * mints as soon as something subscribes to it. A request made through a client whose
   * credential holds this engine waits for that token while none is held, and the client's
   * `credentialScope` reads the same flag, so the scope a read is keyed under says a session
   * wherever the read carries one.
   */
  readonly isLoading: boolean;
  /** The current token, or undefined before the first mint completes. */
  readonly token: string | undefined;
  /**
   * The user the current token was minted for, its mint's `user_id`: `null` where it names no
   * user, and undefined where no token is held or the mint did not say. A client whose credential
   * holds this engine refuses locally a call that acts as a member while it reads `null`, since
   * the API would answer that call with a 401.
   */
  readonly userId: string | null | undefined;
}

/** SessionTokenEngineOptions are what a {@link SessionTokenEngine} is built with. */
export interface SessionTokenEngineOptions {
  /**
   * Set false to stop minting, while signed out for example.
   *
   * Disabling clears the token, its expiry and the last error, so a signed-out consumer cannot
   * keep reading the signed-in user's credential off the snapshot. {@link
   * SessionTokenEngine.refresh} is a no-op while disabled.
   *
   * @default true
   */
  readonly enabled?: boolean | undefined;
  /**
   * Mints a token by calling your server, which holds the secret key.
   *
   * Your endpoint should call `POST /v1/sessions` with `{ publishable_key }` and return the
   * `{ token, expires_at, user_id }` it receives. A call that acts as a member, as every
   * `studio-embedded` operation and every `management` write does, needs a token minted for that
   * member: your endpoint adds `user_id`, the user it signed in, and every request made with the
   * token acts as that user. It reads that user from its own session and never from the request,
   * so a page cannot ask for a token naming somebody else. Reject where the endpoint fails: an
   * answer that holds no token string, or no `expires_at` to read, is taken for a failed mint, so
   * `error` is set, the token in hand is kept, and the mint is tried again a minute later.
   * `expires_at` is read as a `Date`, as the string JSON makes of one, or as a number of
   * milliseconds since the epoch, and `user_id` as the snapshot's `userId`. A token for an
   * Embedded Studio, the editor and its read-only viewer alike, is minted with
   * `surface: "studio-embedded"` beside them.
   */
  readonly mint: () => Promise<Session>;
}

/** The name a refusal says an option was passed to. */
const CONTEXT = "SessionTokenEngine";

/**
 * assertMint refuses a `mint` that is not a function, which would otherwise be called at the first
 * mint and reported through a snapshot's error, far from the line that wrote it.
 *
 * @throws `MountayaConfigurationError` naming `mint` and the value.
 */
const assertMint = (mint: unknown): void => {
  if (typeof mint !== "function") {
    throw refuseOption(
      "mint",
      mint,
      "pass a function that calls your session endpoint and answers its { token, expires_at, user_id }.",
      CONTEXT,
    );
  }
};

/**
 * assertEnabled refuses an `enabled` that is neither a boolean nor `undefined`.
 *
 * @throws `MountayaConfigurationError` naming `enabled` and the value.
 */
const assertEnabled = (enabled: unknown): void => {
  if (enabled !== undefined && typeof enabled !== "boolean") {
    throw refuseOption(
      "enabled",
      enabled,
      "pass true to mint, false to stop, or leave it out to mint.",
      CONTEXT,
    );
  }
};

/** The snapshot of an engine that is disabled or disposed: no token, and none on its way. */
const emptySnapshot: SessionTokenSnapshot = {
  error: undefined,
  expiresAt: undefined,
  isLoading: false,
  token: undefined,
  userId: undefined,
};

/**
 * The instant a timer is armed for, and what it does when that instant is reached: a scheduled
 * mint, or the abandonment of a mint that has not settled.
 */
interface Deadline {
  readonly at: number;
  readonly fire: () => void;
  /**
   * Whether the deadline is the retry of a mint that failed or was abandoned. Such a wait is the
   * engine's own guess at when the endpoint answers again, so
   * {@link SessionTokenEngine.checkNow} does not sit it out.
   */
  readonly retriesFailure: boolean;
}

/** A mint call that is out, with the instant it started at. */
interface MintCall {
  readonly answer: Promise<unknown>;
  readonly startedAt: number;
}

/**
 * surfaceFault hands a listener's own fault to the runtime as an uncaught error, on a microtask.
 *
 * A listener that throws is a defect of the program that subscribed it. Thrown through the
 * engine it would read as the mint failing, and dropped it would hide the defect, so it leaves
 * the engine's flow and reaches the program's own uncaught-error handling.
 */
const surfaceFault = (fault: unknown): void => {
  queueMicrotask(() => {
    throw fault;
  });
};

/**
 * SessionTokenEngine keeps a session token fresh: it mints through the application's own
 * endpoint, refreshes a margin before expiry, backs off from tokens that arrive already spent,
 * and abandons a mint that does not settle.
 *
 * A credential takes it as its `sessionToken`, where a request made while its first mint is out
 * waits for the token: {@link SessionTokenSource} states that contract and shows the wiring.
 *
 * `getSnapshot` and `subscribe` are `useSyncExternalStore`'s two arguments; the first subscriber
 * starts it and the last unsubscribe parks it, and {@link SessionTokenEngine.dispose} latches it
 * for an owner that is not a component. One mint is awaited at a time: a
 * {@link SessionTokenEngine.refresh} or a {@link SessionTokenEngine.checkNow} while one is out
 * joins it, and so does a subscriber that resumes the engine while the mint a park left behind is
 * still out. A mint retired by a disable or an abandonment writes nothing when it answers, and
 * one a park retired writes only for the subscriber that resumed it.
 *
 * A mint's answer is read through {@link readSessionAnswer}: one that holds no token, or no
 * expiry to read, is a failed mint. A listener that throws changes nothing here: every listener
 * is still told, and the fault reaches the runtime as the program's own uncaught error.
 */
export class SessionTokenEngine {
  private mintFn: () => Promise<Session>;
  private enabled: boolean;

  private snapshot: SessionTokenSnapshot;
  private readonly listeners = new Set<() => void>();

  /**
   * Whether anything is watching. An engine with no listener mints nothing: {@link
   * SessionTokenEngine.subscribe} says why that is the lifecycle rather than a method pair.
   */
  private parked = true;

  /** The latch an owner that is not a component closes. See {@link dispose}. */
  private disposed = false;

  private timer: ReturnType<typeof setTimeout> | null = null;

  /**
   * What the armed timer is for, held as the instant rather than the timer, so
   * {@link SessionTokenEngine.checkNow} can re-read the clock against it. Undefined where nothing
   * is armed.
   */
  private deadline: Deadline | undefined = undefined;

  /**
   * Whether a mint of the current generation is out. It is what a refresh joins: a mint already
   * on its way answers the question a second one would ask.
   */
  private minting = false;

  /**
   * The mint call that has not answered yet, kept across a park so that a resume joins it rather
   * than calling the application's endpoint a second time. A disable and an abandonment drop it:
   * the next mint is then a call of its own.
   */
  private call: MintCall | undefined = undefined;

  /**
   * A generation counter, so a mint still in flight when the engine parks, is disabled or
   * abandons it cannot write its result over a newer one. Comparing generations is what makes an
   * out-of-order response harmless rather than a token that goes backwards.
   */
  private generation = 0;

  /**
   * How many tokens in a row have arrived with no usable lifetime left, which is what the wait
   * between them doubles on. Only the success branch below the generation check touches it: a
   * retired mint must not be able to stretch the schedule of the run that replaced it, for the
   * same reason it must not be able to write its token.
   */
  private backoff = 0;

  /**
   * Builds an engine over the application's `mint`. It mints nothing until something subscribes.
   *
   * @param options - The `mint` callback, and whether minting starts `enabled`.
   * @throws `MountayaConfigurationError` for options that are not an object, a `mint` that is not
   *   a function, or an `enabled` that is neither a boolean nor `undefined`, naming the option and
   *   the value.
   */
  constructor(options: SessionTokenEngineOptions) {
    // Typed as the options, and still whatever a JavaScript caller passed.
    const given: unknown = options;
    if (typeof given !== "object" || given === null) {
      throw new MountayaConfigurationError(
        `${CONTEXT}: options is ${describeType(given)}; pass { mint }, a function that calls your session endpoint.`,
      );
    }

    assertMint(options.mint);
    assertEnabled(options.enabled);
    this.mintFn = options.mint;
    this.enabled = options.enabled ?? true;
    // Enabled, the engine mints on its first subscriber, so a token is on its way from here.
    this.snapshot = this.enabled ? { ...emptySnapshot, isLoading: true } : emptySnapshot;
  }

  /** getSnapshot returns the current snapshot, by reference and stable between changes. */
  getSnapshot(): SessionTokenSnapshot {
    return this.snapshot;
  }

  /**
   * subscribe registers a listener for every change, and returns its own unsubscribe.
   *
   * **The listeners are the lifecycle.** A mint fires when minting is enabled and somebody is
   * watching; dropping the last listener retires the in-flight mint and clears the timer, and
   * subscribing again starts a fresh cycle. That is what a credential with nobody holding it
   * should do anyway, and it is the only teardown a React binding can express correctly: React
   * tears an effect down and sets it up again with the same object under StrictMode, so a latch
   * here would leave a live surface bound to a dead engine. {@link dispose} is the latch,
   * for an owner that is not a component.
   *
   * A fresh cycle joins the mint the park left out, where that mint has been out for less than
   * the refresh margin: the doubled effect of a development mount then costs the application's
   * endpoint one call, and the mint is still abandoned a margin after it started.
   *
   * **A binding must hand its framework a stable `subscribe`.** A fresh closure per render
   * unsubscribes and re-subscribes on every one, and with the lifecycle on this method that is
   * a mint retired and re-fired per render.
   *
   * @param listener - Called after every change of the snapshot, with no argument.
   * @returns The function that removes this listener, and parks the engine when it is the last.
   */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);

    if (this.parked && !this.disposed) {
      this.parked = false;
      void this.run();
    }

    return () => {
      this.listeners.delete(listener);

      if (this.listeners.size > 0 || this.parked) {
        return;
      }

      this.parked = true;
      this.park();
    };
  }

  /**
   * refresh mints now, replacing any pending refresh, or joins the mint already out.
   *
   * Joining is what keeps a burst of refreshes, a retry button pressed twice or several surfaces
   * asking at once, to one call to the application's endpoint: the mint on its way answers every
   * one of them. The current token stays readable until the new one arrives, so a consumer
   * holding a client built from it keeps working across the call rather than losing its
   * credential for the duration of a round trip. A no-op while disabled or parked.
   *
   * Deliberately not a backoff reset. A retry button is exactly what a user presses while the
   * endpoint is handing out expired tokens, so resetting here would hand them a way to hold the
   * interval at five seconds by pressing it: the protection is for the application's endpoint,
   * and a caller cannot be the one to lift it.
   */
  refresh(): void {
    if (this.minting) {
      return;
    }

    void this.run();
  }

  /**
   * checkNow re-reads the wall clock against the armed deadline: one already passed fires now,
   * and one still ahead is re-armed from the clock as it reads now.
   *
   * It is what a binding calls when the page comes back (visible again, online again, restored
   * from the back-forward cache), because a suspended machine runs no timer. It wakes with the
   * clock past the deadline and the next hop still minutes away, holding a token that may have
   * expired during the sleep, and this replaces it before a request carries it. A mint already
   * out is joined: its own deadline is re-read, and it is abandoned only once it has been out
   * longer than the margin. A no-op while disabled, parked or disposed.
   *
   * The one deadline it does not wait for is the retry of a mint that failed or was abandoned:
   * that mint is made now. A machine that wakes offline mints and fails, and the page coming back
   * online is the moment the mint can succeed, a margin before the retry would have tried it.
   */
  checkNow(): void {
    const deadline = this.deadline;
    if (!this.enabled || this.parked || this.disposed || deadline === undefined) {
      return;
    }

    if (Date.now() < deadline.at && !deadline.retriesFailure) {
      this.armAt(deadline);

      return;
    }

    this.disarm();
    deadline.fire();
  }

  /**
   * setMint replaces the callback without disturbing the schedule.
   *
   * A binding calls this on every commit, from an effect, because the callback is usually an
   * inline arrow and therefore a new function each render. Replacing it must not re-mint: an
   * engine that restarted its cycle whenever the closure changed would loop, and the loop
   * reads like a server problem rather than like a dependency-list one.
   *
   * @throws `MountayaConfigurationError` for a `mint` that is not a function.
   */
  setMint(mint: () => Promise<Session>): void {
    assertMint(mint);
    this.mintFn = mint;
  }

  /**
   * setEnabled turns minting on or off.
   *
   * Disabling retires the in-flight mint, clears the timer and returns the snapshot to the
   * shape it had before the first mint, so a signed-out consumer cannot keep reading the
   * signed-in user's credential. The error goes with the token: `error` promises a retry is
   * scheduled, and while disabled none is. The backoff goes too: it counts one run of
   * unusable tokens, and a consumer signing back in later is starting a new run rather than
   * continuing that one.
   *
   * Setting the value it already holds does nothing, which is what lets a binding call it from
   * an effect without guarding.
   *
   * @throws `MountayaConfigurationError` for a value that is neither a boolean nor `undefined`,
   *   which reads as `true`, the option's default.
   */
  setEnabled(enabled: boolean): void {
    // Typed as a boolean, and still whatever a JavaScript caller passed: `undefined` reads as the
    // option's own default, which is to mint.
    const given: unknown = enabled;
    assertEnabled(given);
    const next = given !== false;
    if (this.enabled === next) {
      return;
    }

    this.enabled = next;

    if (!next) {
      this.retire();
      // The mint in flight belongs to the session that ends here, and the next one enabled may
      // be another person's, so it is never joined.
      this.call = undefined;
      this.backoff = 0;
      // One commit rather than two: `emptySnapshot` already reports idle, so the fix-up
      // `park` does for a teardown is not needed on this path.
      this.commit(emptySnapshot);

      return;
    }

    if (this.disposed) {
      return;
    }

    if (!this.parked) {
      void this.run();

      return;
    }

    // Nobody holds the engine, so nothing mints yet; its next subscriber mints at once.
    this.reportResting();
  }

  /**
   * dispose retires the in-flight mint, drops the timer, clears the token and latches the
   * engine shut.
   *
   * For an owner that is not a component. A React binding must NOT call this: unsubscribing is
   * its teardown, because StrictMode tears an effect down and sets it up again with the same
   * object. The snapshot returns to the shape a disabled engine has, so a client whose credential
   * still holds the engine sends no token from here, and every listener is told of it before the
   * listeners are dropped.
   */
  dispose(): void {
    if (this.disposed) {
      return;
    }

    this.disposed = true;
    this.parked = true;
    this.retire();
    this.call = undefined;
    this.backoff = 0;
    this.commit(emptySnapshot);
    this.listeners.clear();
  }

  // -----------------------------------------------------------------------------------------

  /**
   * commit replaces the snapshot and notifies, skipping a notification when nothing moved.
   *
   * It never throws: every listener is told, and the first fault one of them throws is handed to
   * {@link surfaceFault} once they all have been, so no caller here has to decide what a
   * listener's exception does to a mint.
   */
  private commit(next: SessionTokenSnapshot): void {
    if (next === this.snapshot) {
      return;
    }

    this.snapshot = next;

    let fault: { readonly thrown: unknown } | undefined;
    for (const listener of this.listeners) {
      try {
        listener();
      } catch (thrown) {
        fault ??= { thrown };
      }
    }

    if (fault !== undefined) {
      surfaceFault(fault.thrown);
    }
  }

  /** disarm drops the armed timer and the deadline it was for. */
  private disarm(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }

    this.deadline = undefined;
  }

  /** retire drops the timer and bumps the generation, so nothing in flight can still write. */
  private retire(): void {
    this.generation += 1;
    this.minting = false;
    this.disarm();
  }

  /** park retires whatever is in flight and reports what a request made now would wait for. */
  private park(): void {
    this.retire();
    this.reportResting();
  }

  /**
   * reportResting sets `isLoading` for an engine nobody holds: a token is on its way where the
   * engine is enabled, not disposed and holds none, since its next subscriber mints at once, and
   * none is otherwise.
   *
   * A teardown has to set the flag itself. `run`'s own settle lowers it only for the current
   * generation, which is correct while a NEWER run is taking over, since that one raised the flag
   * for itself; but parking bumps the generation and starts nothing. Without this, a park while a
   * mint is out leaves the flag up with a token held, and a request made then would read a token
   * as both held and on its way.
   */
  private reportResting(): void {
    const onItsWay = this.enabled && !this.disposed && this.snapshot.token === undefined;
    if (this.snapshot.isLoading !== onItsWay) {
      this.commit({ ...this.snapshot, isLoading: onItsWay });
    }
  }

  /**
   * armAt arms a deadline, replacing whatever was armed.
   *
   * The deadline is held as an absolute instant and walked down in MAX_TIMER_MS hops, so what
   * survives a throttled or suspended tab is the DEADLINE rather than a timer. Each hop
   * re-reads the clock; the deadline fires on the first hop that finds it passed.
   */
  private armAt(deadline: Deadline): void {
    this.disarm();
    this.deadline = deadline;

    const hop = (): void => {
      const remaining = deadline.at - Date.now();
      if (remaining <= 0) {
        this.timer = null;
        this.deadline = undefined;
        deadline.fire();

        return;
      }

      this.timer = setTimeout(hop, Math.min(MAX_TIMER_MS, remaining));
    };

    this.timer = setTimeout(hop, Math.min(MAX_TIMER_MS, Math.max(0, deadline.at - Date.now())));
  }

  /**
   * armRefresh arms the next mint `delay` milliseconds from now, replacing whatever was armed.
   * `retriesFailure` marks the wait after a mint that failed or was abandoned.
   */
  private armRefresh(delay: number, retriesFailure = false): void {
    this.armAt({
      at: Date.now() + delay,
      fire: () => {
        void this.run();
      },
      retriesFailure,
    });
  }

  /**
   * callOut answers the mint call a park left out, where a run can still join it: one that
   * started less than a margin ago. A call out for a margin or longer is the one {@link abandon}
   * gives up on, so it is not joined.
   */
  private callOut(): MintCall | undefined {
    const out = this.call;

    return out !== undefined && Date.now() - out.startedAt < REFRESH_MARGIN_MS ? out : undefined;
  }

  /**
   * startCall calls the application's mint and holds the call until it answers. A mint that
   * throws rather than rejecting is read as one that rejected.
   */
  private startCall(): MintCall {
    let answer: Promise<unknown>;
    try {
      answer = Promise.resolve(this.mintFn());
    } catch (cause) {
      answer = Promise.reject(cause);
    }

    const call: MintCall = { answer, startedAt: Date.now() };
    this.call = call;

    const settle = (): void => {
      if (this.call === call) {
        this.call = undefined;
      }
    };
    answer.then(settle, settle);

    return call;
  }

  /**
   * abandon gives up on the mint of `generation`, still out a margin after it started: it is
   * retired so a late answer writes nothing, the failure is recorded, and a mint is tried again a
   * margin later, as after one that failed outright.
   *
   * The margin is the bound because it is the headroom a token is refreshed with: a mint out for
   * longer has cost the token that headroom, and one that never settles would otherwise hold the
   * engine loading, with nothing scheduled, until the token lapsed.
   */
  private abandon(generation: number): void {
    if (generation !== this.generation) {
      return;
    }

    this.retire();
    this.call = undefined;
    // Armed before the commit, so a listener that refreshes from inside it starts a run that
    // replaces this schedule rather than one this schedule replaces.
    this.armRefresh(REFRESH_MARGIN_MS, true);
    this.commit({
      ...this.snapshot,
      error: new MountayaConnectionError(
        `mint: no answer within ${REFRESH_MARGIN_MS} ms, so the mint is abandoned and tried again in ${REFRESH_MARGIN_MS} ms. Check that the endpoint it calls answers.`,
      ),
      isLoading: false,
    });
  }

  /** run mints once and schedules the next attempt from what came back. */
  private async run(): Promise<void> {
    // A run that cannot start retires nothing either, hence the guard ahead of the generation
    // bump. It covers a `refresh` called while disabled, an engine with nobody watching, and
    // a timer that outlived either flag flipping.
    if (!this.enabled || this.parked || this.disposed) {
      return;
    }

    this.generation += 1;
    const generation = this.generation;
    const isCurrent = (): boolean => this.generation === generation;

    // Armed before the commit, so a listener that reads the snapshot finds the mint already
    // bounded; and before the mint is called, so one that never settles is still abandoned. A
    // mint a park left out is bounded from the instant it started, not from this resume.
    this.minting = true;
    const joined = this.callOut();
    this.armAt({
      at: (joined?.startedAt ?? Date.now()) + REFRESH_MARGIN_MS,
      fire: () => {
        this.abandon(generation);
      },
      retriesFailure: false,
    });
    this.commit({ ...this.snapshot, isLoading: true });

    // A listener that disabled or parked the engine from inside the commit retired this run.
    if (!isCurrent()) {
      return;
    }

    const call = joined ?? this.startCall();

    let session: SessionAnswer;
    try {
      // An answer that is no session is a failed mint, as a rejection is: see
      // `readSessionAnswer` for what one must hold.
      session = readSessionAnswer(await call.answer);
    } catch (cause) {
      if (!isCurrent()) {
        return;
      }

      this.minting = false;
      // Retry on the margin rather than giving up: a failed mint usually means the application's
      // endpoint was briefly unavailable, not that it never will be. Armed before the commit,
      // so a listener that refreshes from inside it replaces this schedule rather than being
      // replaced by it.
      this.armRefresh(REFRESH_MARGIN_MS, true);
      this.commit({ ...this.snapshot, error: cause, isLoading: false });

      return;
    }

    if (!isCurrent()) {
      return;
    }

    this.minting = false;

    // An expiry that names no instant is the case worth handling explicitly rather than letting
    // the arithmetic below run on it. `getTime()` on an Invalid Date is NaN, NaN survives
    // Math.min unchanged and loses every comparison it is put through, so a mis-formatted date
    // would read as a token with no lifetime left: one page load, and the engine spends the
    // life of the process re-minting a perfectly good token against the application's OWN
    // endpoint. The token itself is still usable; only its lifetime is unknown, so it is kept
    // and re-minted on the margin.
    const expiry = session.expiresAt;

    this.commit({
      error: undefined,
      expiresAt: expiry,
      isLoading: false,
      token: session.token,
      userId: session.userId,
    });

    // A listener that refreshed from inside the commit started a run of its own, which owns
    // the schedule and the backoff from here.
    if (!isCurrent()) {
      return;
    }

    // What the schedule is, as against when to fire it: `refreshSchedule` owns the margin, the
    // floor and the doubling, and this method owns the timer they feed. The arithmetic exists
    // once, in the rule `transport.session.refresh` runs against.
    const lifetime = expiry === undefined ? undefined : expiry.getTime() - Date.now();
    const schedule = refreshSchedule({ lifetimeMs: lifetime });

    if (schedule.armed) {
      // The token brought headroom of its own, so the schedule is the token's rather than the
      // engine's. Clearing the count here is what makes the backoff describe the CURRENT
      // run of bad tokens: a skew that clears costs one short wait, not a schedule the process
      // stays stretched on for the rest of its life.
      this.backoff = 0;
      this.armRefresh(schedule.delayMs);

      return;
    }

    // Nothing left to take a proportional margin out of: the token is already spent, or the
    // clock is far enough out of step to look that way. Waiting the floor every time would
    // still cost the application's own endpoint a call every five seconds for as long as that
    // lasts, so each one in a row waits twice as long as the last.
    this.backoff += 1;
    this.armRefresh(refreshSchedule({ consecutiveSpent: this.backoff }).delayMs);
  }
}
